import { useState } from 'react';
import {
  Settings,
  Shield,
  Database,
  Clock,
  Globe,
  Bell,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    introductionExpiry: 30, // days
    maxPendingRequests: 5,
    autoDeclineExpired: true,
    emailNotifications: true,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-600">Configure platform behavior and policies</p>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>

        <div className="grid gap-4 md:grid-cols-3">
          <StatusCard
            title="API Server"
            status="healthy"
            detail="Running on port 3001"
            icon={Globe}
          />
          <StatusCard
            title="Database"
            status="healthy"
            detail="Supabase PostgreSQL"
            icon={Database}
          />
          <StatusCard
            title="Email Service"
            status="healthy"
            detail="Resend API active"
            icon={Bell}
          />
        </div>
      </div>

      {/* Data Sources & Crawler Schedule */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Sources & Crawler Schedule</h2>
        <p className="text-sm text-gray-600 mb-6">
          The crawler automatically collects buyer intelligence signals from multiple sources on a scheduled basis.
        </p>

        <div className="space-y-4">
          <CrawlerScheduleItem
            name="SEC Edgar Filings"
            schedule="3 times daily"
            times="6:00 AM, 12:00 PM, 6:00 PM"
            description="SEC filings - check 3x daily for new 10-K, 8-K filings"
          />
          <CrawlerScheduleItem
            name="M&A News"
            schedule="Every 4 hours"
            times="Continuous"
            description="M&A news from multiple sources - every 4 hours"
          />
          <CrawlerScheduleItem
            name="Yahoo Finance"
            schedule="Daily"
            times="7:30 AM"
            description="Stock prices and financial metrics - daily after market"
          />
          <CrawlerScheduleItem
            name="BioPharma Catalyst"
            schedule="Daily"
            times="6:30 AM"
            description="FDA calendar and catalysts - daily"
          />
          <CrawlerScheduleItem
            name="Clinical Trials"
            schedule="Daily"
            times="8:00 AM"
            description="ClinicalTrials.gov updates - daily"
          />
          <CrawlerScheduleItem
            name="Earnings Calls"
            schedule="Daily"
            times="10:00 AM"
            description="Earnings call transcripts - daily"
          />
          <CrawlerScheduleItem
            name="Conference Tracker"
            schedule="Twice weekly"
            times="Monday & Thursday, 9:00 AM"
            description="Industry conferences - twice weekly"
          />
          <CrawlerScheduleItem
            name="FDA Orange Book"
            schedule="Weekly"
            times="Monday, 5:00 AM"
            description="Patent expiry data - weekly (FDA updates monthly)"
          />
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">How It Works</p>
                <p className="text-sm text-blue-700 mt-1">
                  The crawler runs automatically in the background. New signals are collected and added to buyer profiles.
                  Signals appear in the "Buyer Intelligence & Signals" section when viewing buyer matches.
                  If you see the same data repeatedly, the crawler may not be running or no new data is available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Introduction Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Introduction Request Settings</h2>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Request Expiration</p>
              <p className="text-sm text-gray-500">
                Introduction requests expire after this many days if not processed
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.introductionExpiry}
                onChange={(e) => setSettings({ ...settings, introductionExpiry: parseInt(e.target.value) })}
                min={7}
                max={90}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              <span className="text-gray-500">days</span>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Max Pending Requests per User</p>
                <p className="text-sm text-gray-500">
                  Maximum number of pending requests a user can have at once
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={settings.maxPendingRequests}
                  onChange={(e) => setSettings({ ...settings, maxPendingRequests: parseInt(e.target.value) })}
                  min={1}
                  max={20}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <span className="text-gray-500">requests</span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Auto-Decline Expired Requests</p>
                <p className="text-sm text-gray-500">
                  Automatically decline requests that reach their expiration date
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.autoDeclineExpired}
                  onChange={(e) => setSettings({ ...settings, autoDeclineExpired: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Email Notifications</p>
                <p className="text-sm text-gray-500">
                  Send email notifications for introduction request status changes
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            Save Settings
          </button>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Security</h2>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
            <Shield className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">JWT Authentication Enabled</p>
              <p className="text-sm text-green-600">User sessions are secured with JWT tokens</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
            <Shield className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Rate Limiting Active</p>
              <p className="text-sm text-green-600">API requests are rate-limited to prevent abuse</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
            <Shield className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">CORS Configured</p>
              <p className="text-sm text-green-600">Only allowed origins can access the API</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Environment Configuration</p>
            <p className="text-sm text-yellow-700 mt-1">
              Some settings require changes to the server environment variables.
              Contact your system administrator for advanced configuration changes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ title, status, detail, icon: Icon }) {
  const isHealthy = status === 'healthy';

  return (
    <div className={`p-4 rounded-lg border ${
      isHealthy ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${isHealthy ? 'text-green-600' : 'text-red-600'}`} />
        <span className={`flex items-center gap-1 text-sm ${
          isHealthy ? 'text-green-600' : 'text-red-600'
        }`}>
          <CheckCircle className="w-4 h-4" />
          {status}
        </span>
      </div>
      <p className={`font-medium ${isHealthy ? 'text-green-800' : 'text-red-800'}`}>{title}</p>
      <p className={`text-sm ${isHealthy ? 'text-green-600' : 'text-red-600'}`}>{detail}</p>
    </div>
  );
}

function CrawlerScheduleItem({ name, schedule, times, description }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
      <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
        <RefreshCw className="w-5 h-5 text-purple-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-4 mb-1">
          <h3 className="font-medium text-gray-900">{name}</h3>
          <span className="text-sm font-medium text-purple-600 whitespace-nowrap">{schedule}</span>
        </div>
        <p className="text-sm text-gray-600 mb-1">
          <span className="font-medium">Runs at:</span> {times}
        </p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
  );
}
